import { PageContainer, PageHeader, Spread, Text, VStack } from '@/components/Atoms'
import { Badge } from '@/components/Badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Card'
import { DetailItem } from '@/components/DetailItem'
import { Spinner } from '@/components/Spinner'
import {
  googleFinancialStatementErpDetailQueryOptions,
  useGoogleFinancialStatementErpDetail,
} from '@/hooks/queries/credit/useGoogleFinancialStatements'
import { NO_DATA_AVAILABLE_SYMBOL } from '@/lib/utils/constants'
import {
  GOOGLE_FINANCIAL_STATEMENT_TYPES,
  GOOGLE_FINANCIAL_STATEMENTS_DEFAULT_YEAR_SPAN,
  GoogleFinancialStatementType,
} from '@repo/constants'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TooltipProvider,
} from '@repo/ui/components'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { GfsMetricCard } from './-components/GfsMetricCard'
import { GoogleFinancialStatementDetailDialog } from './-components/GoogleFinancialStatementDetailDialog'
import { buildStatementWindow } from './-components/statementWindow'

const detailSearchSchema = z.object({
  // Focus year — re-scopes every card's window. Lives in the URL so the view is shareable.
  year: z.number().optional(),
})

export const Route = createFileRoute('/_auth/_modules/credit/google-financial-statements/$sapErp')({
  component: RouteComponent,
  validateSearch: (search) => detailSearchSchema.parse(search),
  loader: async ({ context, params: { sapErp } }) => {
    const detail = await context.queryClient.ensureQueryData(
      googleFinancialStatementErpDetailQueryOptions(sapErp),
    )
    return { crumb: detail.metadata?.businessName ?? sapErp }
  },
})

function RouteComponent() {
  const { t } = useTranslation(['credit', 'common'])
  const { sapErp } = Route.useParams()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const detailQuery = useGoogleFinancialStatementErpDetail(sapErp)
  const [selectedType, setSelectedType] = useState<GoogleFinancialStatementType | null>(null)

  const metadata = detailQuery.data?.metadata ?? null
  const availableYears = detailQuery.data?.availableYears ?? []
  const cells = detailQuery.data?.cells ?? []

  const latestYear = availableYears.length ? Math.max(...availableYears) : new Date().getFullYear()
  const selectedYear = search.year ?? latestYear
  // Years before the dataset's first year are known-empty, so they're dropped from the window.
  const earliestDataYear = availableYears.length ? Math.min(...availableYears) : null

  const detailItems = [
    { label: t('googleFinancialStatements.detailPage.erpNumber'), value: sapErp },
    {
      label: t('googleFinancialStatements.detailPage.legalName'),
      value: metadata?.legalName ?? NO_DATA_AVAILABLE_SYMBOL,
    },
    {
      label: t('googleFinancialStatements.detailPage.stockSymbol'),
      value: metadata?.stockSymbol ?? NO_DATA_AVAILABLE_SYMBOL,
    },
    {
      label: t('googleFinancialStatements.detailPage.customerGroup'),
      value: metadata?.creditCustomerGroup ?? NO_DATA_AVAILABLE_SYMBOL,
    },
    {
      label: t('googleFinancialStatements.detailPage.fiscalDate'),
      value: metadata?.financialExerciceDate ?? NO_DATA_AVAILABLE_SYMBOL,
    },
  ]

  return (
    <PageContainer maxWidth="wide">
      <Spread align="start">
        <VStack gap="sm">
          <PageHeader>{metadata?.businessName ?? sapErp}</PageHeader>
          <Text tone="muted" weight="medium">
            {t('googleFinancialStatements.detailPage.erpNumber')} {sapErp}
          </Text>
        </VStack>

        {availableYears.length > 0 && (
          <Select
            value={String(selectedYear)}
            onValueChange={(value) => navigate({ search: { year: Number(value) } })}
          >
            <SelectTrigger className="bg-background w-[140px]">
              <SelectValue placeholder={t('googleFinancialStatements.detailPage.selectYear')} />
            </SelectTrigger>
            <SelectContent>
              {[...availableYears]
                .sort((a, b) => b - a)
                .map((year) => (
                  <SelectItem key={year} value={String(year)}>
                    {year}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        )}
      </Spread>

      {detailQuery.isPending ? (
        <div className="flex justify-center py-16">
          <Spinner size="sm" />
        </div>
      ) : metadata === null ? (
        <Text tone="muted" className="block py-16 text-center">
          {t('googleFinancialStatements.detailPage.empty')}
        </Text>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>{t('googleFinancialStatements.detailPage.detailsTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6 md:grid-cols-3">
                {detailItems.map((item) => (
                  <DetailItem key={item.label} label={item.label}>
                    {item.value}
                  </DetailItem>
                ))}
                <DetailItem label={t('googleFinancialStatements.detailPage.tradedStatus')}>
                  {metadata.isTradedPublicly === null ? (
                    NO_DATA_AVAILABLE_SYMBOL
                  ) : (
                    <Badge tone={metadata.isTradedPublicly ? 'info' : 'default'} className="w-min">
                      {metadata.isTradedPublicly
                        ? t('googleFinancialStatements.detailPage.public')
                        : t('googleFinancialStatements.detailPage.private')}
                    </Badge>
                  )}
                </DetailItem>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('googleFinancialStatements.detailPage.trendsTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <TooltipProvider delayDuration={150}>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {GOOGLE_FINANCIAL_STATEMENT_TYPES.map((type) => (
                    <GfsMetricCard
                      key={type}
                      type={type}
                      selectedYear={selectedYear}
                      cells={cells}
                      onOpenHistory={setSelectedType}
                    />
                  ))}
                </div>
              </TooltipProvider>
            </CardContent>
          </Card>
        </>
      )}

      <GoogleFinancialStatementDetailDialog
        open={selectedType !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedType(null)
        }}
        erp={metadata ? { sapErp, businessName: metadata.businessName } : null}
        type={selectedType ?? GOOGLE_FINANCIAL_STATEMENT_TYPES[0]}
        values={
          selectedType
            ? buildStatementWindow(
                cells,
                selectedType,
                selectedYear,
                GOOGLE_FINANCIAL_STATEMENTS_DEFAULT_YEAR_SPAN,
              )
                .filter((point) => earliestDataYear === null || point.year >= earliestDataYear)
                .map((point) => ({ year: point.year, value: point.value }))
            : []
        }
      />
    </PageContainer>
  )
}
